import React, { useState, useEffect } from "react";
import clubService from "../../services/clubService";
import categoryService from "../../services/categoryService";
import universityService from "../../services/universityService";
import eventService from "../../services/eventService";
import imageService from "../../services/imageService";
import { useNotification } from "../../context/NotificationContext";
import campusService from "../../services/campusService";

const EventForm = ({ event, onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState({
    titleEn: "",
    titleLocal: "",
    descriptionEn: "",
    descriptionLocal: "",
    universityId: "",
    campusId: "",
    clubId: "",
    categoryIds: [],
    startDate: "",
    endDate: "",
    price: 0,
    participationScope: "PUBLIC",
    participationType: "PHYSICAL",
    addressId: "",
    imageEnId: "",
    imageLocalId: "",
    ticketAttendants: [],
  });

  const [clubs, setClubs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [addresses, setAddresses] = useState({
    myClubAddresses: [],
    otherClubsAddresses: [],
  });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    descriptionEn: "",
    descriptionLocal: "",
    latitude: "",
    longitude: "",
    createdByClubId: "",
    universityId: "",
    campusId: "",
  });

  // Image upload states
  const [, setImageEnFile] = useState(null);
  const [, setImageLocalFile] = useState(null);
  const [imageEnPreview, setImageEnPreview] = useState(null);
  const [imageLocalPreview, setImageLocalPreview] = useState(null);
  const [imageUploading, setImageUploading] = useState({
    en: false,
    local: false,
  });

  const { showSuccess, showError } = useNotification();

  const participationScopes = [
    { value: "CLUB_ONLY", label: "Club Only" },
    { value: "CAMPUS_ONLY", label: "Campus Only" },
    { value: "COUNTRY_ONLY", label: "Country Only" },
    { value: "PUBLIC", label: "Public" },
  ];

  const participationTypes = [
    { value: "ONLINE", label: "Online" },
    { value: "PHYSICAL", label: "Physical" },
  ];

  useEffect(() => {
    loadOptions();
  }, []);

  // Handle event data when editing
  useEffect(() => {
    if (event) {
      console.log("Event data received in form:", event);
      console.log("Event universityId:", event.universityId);
      console.log("Event campusId:", event.campusId);
      console.log("Event clubId:", event.clubId);

      const newFormData = {
        titleEn: event.titleEn || "",
        titleLocal: event.titleLocal || "",
        descriptionEn: event.descriptionEn || "",
        descriptionLocal: event.descriptionLocal || "",
        universityId: event.universityId?.toString() || "",
        campusId: event.campusId?.toString() || "",
        clubId: event.clubId?.toString() || "",
        categoryIds:
          event.categories?.map((cat) => cat.id.toString()) ||
          event.categoryIds?.map((id) => id.toString()) ||
          [],
        startDate: event.startDate ? formatDateForInput(event.startDate) : "",
        endDate: event.endDate ? formatDateForInput(event.endDate) : "",
        price: event.price || 0,
        participationScope: event.participationScope || "PUBLIC",
        participationType: event.participationType || "PHYSICAL",
        addressId: event.addressId?.toString() || "",
        imageEnId: event.imageEnId?.toString() || "",
        imageLocalId: event.imageLocalId?.toString() || "",
        ticketAttendants: event.ticketAttendants || [],
      };

      setFormData(newFormData);
      console.log("Form data set:", newFormData);

      // Load existing image previews
      if (event.imageEnId) {
        setImageEnPreview(imageService.getImageUrl(event.imageEnId));
      }
      if (event.imageLocalId) {
        setImageLocalPreview(imageService.getImageUrl(event.imageLocalId));
      }
    } else {
      console.log("No event data - creating new event");
    }
  }, [event]);

  // Load campuses when university changes
  useEffect(() => {
    if (formData.universityId) {
      loadCampuses(formData.universityId);
    } else {
      setCampuses([]);
      // Only clear campus and club if we're not in edit mode or if university was actually cleared
      if (!event || !event.universityId) {
        setFormData((prev) => ({ ...prev, campusId: "", clubId: "" }));
      }
    }
  }, [formData.universityId, event]);

  // Load clubs when campus changes
  useEffect(() => {
    if (formData.campusId) {
      loadClubs(formData.campusId);
    } else {
      setClubs([]);
      // Only clear club if we're not in edit mode or if campus was actually cleared
      if (!event || !event.campusId) {
        setFormData((prev) => ({ ...prev, clubId: "" }));
      }
    }
  }, [formData.campusId, event]);

  // Load addresses when club changes
  useEffect(() => {
    if (formData.clubId && formData.universityId) {
      loadAddresses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.clubId, formData.universityId]);

  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const loadOptions = async () => {
    try {
      setOptionsLoading(true);
      const [universityResponse, categoryResponse] = await Promise.all([
        universityService.getUniversities(0, 1000),
        categoryService.getAllCategories(),
      ]);

      if (universityResponse.success) {
        setUniversities(universityResponse.data?.data || []);
      }

      if (categoryResponse.success) {
        const categoryData = categoryResponse.data?.content || [];
        console.log("Loaded categories:", categoryData);
        setCategories(categoryData);
      } else {
        console.error("Failed to load categories:", categoryResponse);
        setCategories([]);
      }
    } catch (error) {
      console.error("Error loading options:", error);
      setCategories([]);
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadCampuses = async (universityId) => {
    try {
      setOptionsLoading(true);
      console.log("Loading campuses for university:", universityId);
      const response = await campusService.getCampuses(
        0,
        1000,
        "",
        universityId
      );
      if (response.success) {
        const campusesData = response.data?.data || [];
        console.log("Loaded campuses:", campusesData);
        setCampuses(campusesData);
      }
    } catch (error) {
      console.error("Error loading campuses:", error);
      setCampuses([]);
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadClubs = async (campusId) => {
    try {
      setOptionsLoading(true);
      console.log("Loading clubs for campus:", campusId);
      const response = await clubService.getClubs(0, 1000, "", campusId);
      if (response.success) {
        const clubsData = response.data?.data || [];
        console.log("Loaded clubs:", clubsData);
        setClubs(clubsData);
      }
    } catch (error) {
      console.error("Error loading clubs:", error);
      setClubs([]);
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadAddresses = async () => {
    if (!formData.clubId || !formData.universityId) return;

    try {
      setAddressesLoading(true);
      const response = await eventService.getAvailableAddresses(
        formData.clubId,
        formData.universityId
      );
      if (response.success) {
        setAddresses({
          myClubAddresses: response.data.myClubAddresses || [],
          otherClubsAddresses: response.data.otherClubsAddresses || [],
        });
      } else {
        setAddresses({
          myClubAddresses: [],
          otherClubsAddresses: [],
        });
      }
    } catch (error) {
      console.error("Error loading addresses:", error);
      setAddresses({
        myClubAddresses: [],
        otherClubsAddresses: [],
      });
    } finally {
      setAddressesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    // Handle cascading resets for organization hierarchy
    if (name === "universityId") {
      setFormData((prev) => ({
        ...prev,
        universityId: value,
        campusId: "",
        clubId: "",
        addressId: "",
      }));
    } else if (name === "campusId") {
      setFormData((prev) => ({
        ...prev,
        campusId: value,
        clubId: "",
        addressId: "",
      }));
    } else if (name === "clubId") {
      setFormData((prev) => ({
        ...prev,
        clubId: value,
        addressId: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "number" ? parseFloat(value) || 0 : value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCategoryChange = (categoryId) => {
    setFormData((prev) => {
      const newCategoryIds = prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId];

      return {
        ...prev,
        categoryIds: newCategoryIds,
      };
    });

    // Clear error when user makes a selection
    if (errors.categoryIds) {
      setErrors((prev) => ({
        ...prev,
        categoryIds: "",
      }));
    }
  };

  const handleTicketAttendantChange = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      ticketAttendants: prev.ticketAttendants.map((attendant, i) =>
        i === index ? value : attendant
      ),
    }));
  };

  const addTicketAttendant = () => {
    setFormData((prev) => ({
      ...prev,
      ticketAttendants: [...prev.ticketAttendants, ""],
    }));
  };

  const removeTicketAttendant = (index) => {
    setFormData((prev) => ({
      ...prev,
      ticketAttendants: prev.ticketAttendants.filter((_, i) => i !== index),
    }));
  };

  const handleAddressFormChange = (e) => {
    const { name, value } = e.target;
    setNewAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateAddress = async () => {
    try {
      // Validate required fields
      if (
        !newAddress.longitude ||
        !newAddress.latitude ||
        !newAddress.descriptionEn ||
        !newAddress.descriptionLocal ||
        !formData.clubId
      ) {
        showError("Please fill in all required fields");
        return;
      }

      const addressData = {
        ...newAddress,
        createdByClubId: formData.clubId,
        universityId: formData.universityId,
        campusId: formData.campusId,
      };

      const response = await eventService.createAddress(addressData);
      if (response.success) {
        showSuccess("Address created successfully!");
        setShowAddressForm(false);
        setNewAddress({
          descriptionEn: "",
          descriptionLocal: "",
          latitude: "",
          longitude: "",
          createdByClubId: "",
          universityId: "",
          campusId: "",
        });
        loadAddresses(); // Reload addresses
      }
    } catch (error) {
      console.error("Error creating address:", error);
      showError("Failed to create address");
    }
  };

  // Image handling functions
  const handleImageChange = async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      showError("Please select a valid image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError("Image size must be less than 5MB");
      return;
    }

    try {
      setImageUploading((prev) => ({ ...prev, [imageType]: true }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        if (imageType === "en") {
          setImageEnPreview(e.target.result);
        } else {
          setImageLocalPreview(e.target.result);
        }
      };
      reader.readAsDataURL(file);

      // Upload image
      console.log(`Uploading ${imageType} image...`);
      const response = await imageService.uploadPhoto(file);
      console.log(`Image upload response:`, response);

      if (response.success) {
        // Use the same approach as ClubForm
        const imageId = response.data.imageId;
        console.log(`Setting ${imageType} image ID:`, imageId);
        setFormData((prev) => ({
          ...prev,
          [imageType === "en" ? "imageEnId" : "imageLocalId"]: imageId,
        }));
        showSuccess(
          `${
            imageType === "en" ? "English" : "Local"
          } image uploaded successfully!`
        );
      } else {
        console.error(`Failed to upload ${imageType} image:`, response);
        showError(`Failed to upload ${imageType} image`);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showError("Failed to upload image");
      // Reset preview on error
      if (imageType === "en") {
        setImageEnPreview(null);
      } else {
        setImageLocalPreview(null);
      }
    } finally {
      setImageUploading((prev) => ({ ...prev, [imageType]: false }));
    }
  };

  const removeImage = (imageType) => {
    if (imageType === "en") {
      setImageEnFile(null);
      setImageEnPreview(null);
      setFormData((prev) => ({ ...prev, imageEnId: "" }));
    } else {
      setImageLocalFile(null);
      setImageLocalPreview(null);
      setFormData((prev) => ({ ...prev, imageLocalId: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Local title is required
    if (!formData.titleLocal.trim()) {
      newErrors.titleLocal = "Local title is required";
    }

    // Local description is required
    if (!formData.descriptionLocal.trim()) {
      newErrors.descriptionLocal = "Local description is required";
    }

    // Organization hierarchy validation
    if (!formData.universityId) {
      newErrors.universityId = "University selection is required";
    }

    if (!formData.campusId) {
      newErrors.campusId = "Campus selection is required";
    }

    if (!formData.clubId) {
      newErrors.clubId = "Club selection is required";
    }

    if (formData.categoryIds.length === 0) {
      newErrors.categoryIds = "At least one category is required";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    }

    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) >= new Date(formData.endDate)
    ) {
      newErrors.endDate = "End date must be after start date";
    }

    if (!formData.addressId) {
      newErrors.addressId = "Address selection is required";
    }

    // Images are required
    if (!formData.imageLocalId) {
      newErrors.imageLocalId = "Local image is required";
    }

    if (!formData.imageEnId) {
      newErrors.imageEnId = "English image is required";
    }

    // Validate future dates
    const now = new Date();
    if (formData.startDate && new Date(formData.startDate) <= now) {
      newErrors.startDate = "Start date must be in the future";
    }

    if (formData.endDate && new Date(formData.endDate) <= now) {
      newErrors.endDate = "End date must be in the future";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form data before validation:", formData);
    console.log("Image IDs in formData:", {
      imageEnId: formData.imageEnId,
      imageLocalId: formData.imageLocalId,
    });

    if (validateForm()) {
      const submitData = {
        titleLocal: formData.titleLocal.trim(),
        titleEn: formData.titleEn.trim() || formData.titleLocal.trim(), // Use local if English is empty
        descriptionLocal: formData.descriptionLocal.trim(),
        descriptionEn:
          formData.descriptionEn.trim() || formData.descriptionLocal.trim(), // Use local if English is empty
        universityId: formData.universityId,
        campusId: formData.campusId,
        clubId: formData.clubId,
        categoryIds: formData.categoryIds.map((id) => parseInt(id)),
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        price: formData.price,
        participationScope: formData.participationScope,
        participationType: formData.participationType,
        addressId: parseInt(formData.addressId),
        imageEnId: formData.imageEnId ? parseInt(formData.imageEnId) : null,
        imageLocalId: formData.imageLocalId
          ? parseInt(formData.imageLocalId)
          : null,
        ticketAttendants: formData.ticketAttendants.filter(
          (attendant) => attendant.trim() !== ""
        ),
      };

      console.log("Submitting event data:", submitData);
      console.log("Image IDs in submit data:", {
        imageEnId: submitData.imageEnId,
        imageLocalId: submitData.imageLocalId,
      });
      onSubmit(submitData);
    }
  };

  return (
    <div className="event-form">
      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-info-circle"></i>
            <h6>Basic Information</h6>
          </div>

          <div className="row">
            <div className="col-md-6">
              <label className="form-label fw-medium">
                Title (Local) <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${
                  errors.titleLocal ? "is-invalid" : ""
                }`}
                name="titleLocal"
                value={formData.titleLocal}
                onChange={handleInputChange}
                placeholder="Enter local title"
                disabled={loading}
              />
              {errors.titleLocal && (
                <div className="invalid-feedback">{errors.titleLocal}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-medium">
                Title (English) <span className="text-muted">(Optional)</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.titleEn ? "is-invalid" : ""}`}
                name="titleEn"
                value={formData.titleEn}
                onChange={handleInputChange}
                placeholder="Enter English title (will use local if empty)"
                disabled={loading}
              />
              {errors.titleEn && (
                <div className="invalid-feedback">{errors.titleEn}</div>
              )}
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-6">
              <label className="form-label fw-medium">
                Description (Local) <span className="text-danger">*</span>
              </label>
              <textarea
                className={`form-control ${
                  errors.descriptionLocal ? "is-invalid" : ""
                }`}
                name="descriptionLocal"
                value={formData.descriptionLocal}
                onChange={handleInputChange}
                placeholder="Enter local description"
                rows="3"
                disabled={loading}
              />
              {errors.descriptionLocal && (
                <div className="invalid-feedback">
                  {errors.descriptionLocal}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-medium">
                Description (English){" "}
                <span className="text-muted">(Optional)</span>
              </label>
              <textarea
                className="form-control"
                name="descriptionEn"
                value={formData.descriptionEn}
                onChange={handleInputChange}
                placeholder="Enter English description (will use local if empty)"
                rows="3"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Event Organization */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-users"></i>
            <h6>Event Organization</h6>
          </div>

          <div className="row">
            <div className="col-md-4">
              <label className="form-label fw-medium">
                University <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${
                  errors.universityId ? "is-invalid" : ""
                }`}
                name="universityId"
                value={formData.universityId}
                onChange={handleInputChange}
                disabled={loading || optionsLoading}
              >
                <option value="">Select a university</option>
                {universities.map((university) => (
                  <option key={university.id} value={university.id}>
                    {university.nameEn}
                  </option>
                ))}
              </select>
              {errors.universityId && (
                <div className="invalid-feedback">{errors.universityId}</div>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-medium">
                Campus <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.campusId ? "is-invalid" : ""}`}
                name="campusId"
                value={formData.campusId}
                onChange={handleInputChange}
                disabled={loading || optionsLoading || !formData.universityId}
              >
                <option value="">
                  {!formData.universityId
                    ? "Select a university first"
                    : "Select a campus"}
                </option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.nameEn}
                  </option>
                ))}
              </select>
              {errors.campusId && (
                <div className="invalid-feedback">{errors.campusId}</div>
              )}
              {!formData.universityId && (
                <small className="text-muted">
                  Please select a university first
                </small>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-medium">
                Club <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.clubId ? "is-invalid" : ""}`}
                name="clubId"
                value={formData.clubId}
                onChange={handleInputChange}
                disabled={loading || optionsLoading || !formData.campusId}
              >
                <option value="">
                  {!formData.campusId
                    ? "Select a campus first"
                    : "Select a club"}
                </option>
                {clubs.map((club) => (
                  <option key={club.id} value={club.id}>
                    {club.nameEn || club.name}
                  </option>
                ))}
              </select>
              {errors.clubId && (
                <div className="invalid-feedback">{errors.clubId}</div>
              )}
              {!formData.campusId && (
                <small className="text-muted">
                  Please select a campus first
                </small>
              )}
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-12">
              <label className="form-label fw-medium">
                Categories <span className="text-danger">*</span>
              </label>
              <div
                className={`border rounded p-3 ${
                  errors.categoryIds ? "border-danger" : ""
                }`}
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                <div className="row g-2">
                  {categories.map((category) => (
                    <div key={category.id} className="col-md-4 col-lg-3">
                      <div
                        className="form-check category-item p-2 rounded cursor-pointer"
                        style={{
                          cursor: "pointer",
                          backgroundColor: formData.categoryIds.includes(
                            category.id
                          )
                            ? "rgba(13, 110, 253, 0.1)"
                            : "transparent",
                          transition: "background-color 0.2s",
                        }}
                        onClick={() => handleCategoryChange(category.id)}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`category-${category.id}`}
                          checked={formData.categoryIds.includes(category.id)}
                          onChange={() => handleCategoryChange(category.id)}
                          disabled={loading}
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor={`category-${category.id}`}
                          style={{ cursor: "pointer" }}
                        >
                          {category.name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {errors.categoryIds && (
                <div className="text-danger small mt-1">
                  {errors.categoryIds}
                </div>
              )}
              <div className="text-muted small mt-2">
                Selected categories: {formData.categoryIds.length}
              </div>
            </div>
          </div>
        </div>

        {/* Date and Time */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-calendar-alt"></i>
            <h6>Date and Time</h6>
          </div>

          <div className="row">
            <div className="col-md-6">
              <label className="form-label fw-medium">
                Start Date <span className="text-danger">*</span>
              </label>
              <input
                type="datetime-local"
                className={`form-control ${
                  errors.startDate ? "is-invalid" : ""
                }`}
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                disabled={loading}
              />
              {errors.startDate && (
                <div className="invalid-feedback">{errors.startDate}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-medium">
                End Date <span className="text-danger">*</span>
              </label>
              <input
                type="datetime-local"
                className={`form-control ${errors.endDate ? "is-invalid" : ""}`}
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                disabled={loading}
              />
              {errors.endDate && (
                <div className="invalid-feedback">{errors.endDate}</div>
              )}
            </div>
          </div>
        </div>

        {/* Participation Settings */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-cog"></i>
            <h6>Participation Settings</h6>
          </div>

          <div className="row">
            <div className="col-md-4">
              <label className="form-label fw-medium">
                Participation Scope <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                name="participationScope"
                value={formData.participationScope}
                onChange={handleInputChange}
                disabled={loading}
              >
                {participationScopes.map((scope) => (
                  <option key={scope.value} value={scope.value}>
                    {scope.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-medium">
                Participation Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                name="participationType"
                value={formData.participationType}
                onChange={handleInputChange}
                disabled={loading}
              >
                {participationTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-medium">Price</label>
              <input
                type="number"
                className="form-control"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-map-marker-alt"></i>
            <h6>Location</h6>
          </div>

          <div className="row">
            <div className="col-md-10">
              <label className="form-label fw-medium">
                Address <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${
                  errors.addressId ? "is-invalid" : ""
                }`}
                name="addressId"
                value={formData.addressId}
                onChange={handleInputChange}
                disabled={loading || addressesLoading || !formData.clubId}
              >
                <option value="">
                  {!formData.clubId
                    ? "Select a club first"
                    : addressesLoading
                    ? "Loading addresses..."
                    : addresses.myClubAddresses.length === 0 &&
                      addresses.otherClubsAddresses.length === 0
                    ? "No addresses available"
                    : "Select an address"}
                </option>

                {/* My Club Addresses */}
                {addresses.myClubAddresses.length > 0 && (
                  <optgroup label="My Club Addresses">
                    {addresses.myClubAddresses.map((address) => (
                      <option key={address.id} value={address.id}>
                        {address.description}{" "}
                        {address.descriptionEn
                          ? `(${address.descriptionEn})`
                          : ""}
                      </option>
                    ))}
                  </optgroup>
                )}

                {/* Other Clubs Addresses */}
                {addresses.otherClubsAddresses.length > 0 && (
                  <optgroup label="Other Clubs Addresses">
                    {addresses.otherClubsAddresses.map((address) => (
                      <option key={address.id} value={address.id}>
                        {address.description}{" "}
                        {address.descriptionEn
                          ? `(${address.descriptionEn})`
                          : ""}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {errors.addressId && (
                <div className="invalid-feedback">{errors.addressId}</div>
              )}
            </div>

            <div className="col-md-2">
              <label className="form-label fw-medium">&nbsp;</label>
              <button
                type="button"
                className="btn btn-outline-primary w-100"
                onClick={() => setShowAddressForm(true)}
                disabled={loading || !formData.clubId}
              >
                <i className="fas fa-plus"></i>
              </button>
            </div>
          </div>

          {/* Add Address Form */}
          {showAddressForm && (
            <div className="mt-3 p-3 border rounded bg-light">
              <h6 className="fw-bold mb-3">Add New Address</h6>
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">
                    Description (Local) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="descriptionLocal"
                    value={newAddress.descriptionLocal}
                    onChange={handleAddressFormChange}
                    placeholder="Local description"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Description (English) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="descriptionEn"
                    value={newAddress.descriptionEn}
                    onChange={handleAddressFormChange}
                    placeholder="English description"
                  />
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md-6">
                  <label className="form-label">
                    Latitude <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    name="latitude"
                    value={newAddress.latitude}
                    onChange={handleAddressFormChange}
                    placeholder="Latitude"
                    step="any"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Longitude <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    name="longitude"
                    value={newAddress.longitude}
                    onChange={handleAddressFormChange}
                    placeholder="Longitude"
                    step="any"
                  />
                </div>
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-success btn-sm me-2"
                  onClick={handleCreateAddress}
                  disabled={loading}
                >
                  Create Address
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowAddressForm(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Images */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-image"></i>
            <h6>Event Images</h6>
          </div>

          <div className="row">
            {/* Local Image */}
            <div className="col-md-6">
              <label className="form-label fw-medium">
                Local Image <span className="text-danger">*</span>
              </label>
              <div
                className={`border rounded p-3 ${
                  errors.imageLocalId ? "border-danger" : ""
                }`}
              >
                {imageLocalPreview ? (
                  <div className="text-center">
                    <img
                      src={imageLocalPreview}
                      alt="Local Preview"
                      className="img-fluid rounded mb-2"
                      style={{ maxHeight: "200px", objectFit: "cover" }}
                    />
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeImage("local")}
                        disabled={loading}
                      >
                        <i className="fas fa-trash me-1"></i>
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <i
                      className="fas fa-cloud-upload-alt text-muted mb-2"
                      style={{ fontSize: "2rem" }}
                    ></i>
                    <div>
                      <input
                        type="file"
                        id="imageLocal"
                        className="d-none"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, "local")}
                        disabled={loading || imageUploading.local}
                      />
                      <label
                        htmlFor="imageLocal"
                        className={`btn btn-outline-primary ${
                          imageUploading.local ? "disabled" : ""
                        }`}
                      >
                        {imageUploading.local ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-upload me-2"></i>
                            Choose Local Image
                          </>
                        )}
                      </label>
                    </div>
                    <small className="text-muted d-block mt-2">
                      Max size: 5MB. Supported formats: JPG, PNG, GIF
                    </small>
                  </div>
                )}
              </div>
              {errors.imageLocalId && (
                <div className="text-danger small mt-1">
                  {errors.imageLocalId}
                </div>
              )}
            </div>

            {/* English Image */}
            <div className="col-md-6">
              <label className="form-label fw-medium">
                English Image <span className="text-danger">*</span>
              </label>
              <div
                className={`border rounded p-3 ${
                  errors.imageEnId ? "border-danger" : ""
                }`}
              >
                {imageEnPreview ? (
                  <div className="text-center">
                    <img
                      src={imageEnPreview}
                      alt="English Preview"
                      className="img-fluid rounded mb-2"
                      style={{ maxHeight: "200px", objectFit: "cover" }}
                    />
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeImage("en")}
                        disabled={loading}
                      >
                        <i className="fas fa-trash me-1"></i>
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <i
                      className="fas fa-cloud-upload-alt text-muted mb-2"
                      style={{ fontSize: "2rem" }}
                    ></i>
                    <div>
                      <input
                        type="file"
                        id="imageEn"
                        className="d-none"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, "en")}
                        disabled={loading || imageUploading.en}
                      />
                      <label
                        htmlFor="imageEn"
                        className={`btn btn-outline-primary ${
                          imageUploading.en ? "disabled" : ""
                        }`}
                      >
                        {imageUploading.en ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-upload me-2"></i>
                            Choose English Image
                          </>
                        )}
                      </label>
                    </div>
                    <small className="text-muted d-block mt-2">
                      Max size: 5MB. Supported formats: JPG, PNG, GIF
                    </small>
                  </div>
                )}
              </div>
              {errors.imageEnId && (
                <div className="text-danger small mt-1">{errors.imageEnId}</div>
              )}
            </div>
          </div>

          {/* Quick Copy Button */}
          {imageLocalPreview && !imageEnPreview && (
            <div className="row mt-3">
              <div className="col-md-12">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  You can use the same image for both languages.
                  <button
                    type="button"
                    className="btn btn-outline-info btn-sm ms-2"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        imageEnId: prev.imageLocalId,
                      }));
                      setImageEnPreview(imageLocalPreview);
                    }}
                    disabled={loading}
                  >
                    <i className="fas fa-copy me-1"></i>
                    Use Local Image for English
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Ticket Attendants */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-ticket-alt"></i>
            <h6>Ticket Attendants (Optional)</h6>
          </div>

          <div className="row">
            <div className="col-md-12">
              {formData.ticketAttendants.map((attendant, index) => (
                <div key={index} className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    value={attendant}
                    onChange={(e) =>
                      handleTicketAttendantChange(index, e.target.value)
                    }
                    placeholder="Enter attendant name"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeTicketAttendant(index)}
                    disabled={loading}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={addTicketAttendant}
                disabled={loading}
              >
                <i className="fas fa-plus me-2"></i>
                Add Ticket Attendant
              </button>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || imageUploading.en || imageUploading.local}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                {event ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <i className={`fas ${event ? "fa-save" : "fa-plus"} me-2`}></i>
                {event ? "Update Event" : "Create Event"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
